/**
 * Transaction process graph for bookings:
 *   - default-booking
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes a booking to a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',
  REQUEST_PAYMENT_WITH_PAYPAL: 'transition/request-payment-with-paypal',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',
  REQUEST_PAYMENT_AFTER_INQUIRY_WITH_PAYPAL: 'transition/request-payment-after-inquiry-with-paypal',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',
  CONFIRM_PAYMENT_WITH_PAYPAL: 'transition/confirm-payment-with-paypal',
  RECORD_PAYPAL_AUTHORIZATION: 'transition/record-paypal-authorization',
  UPDATE_PAYPAL_PAYMENT: 'transition/update-paypal-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',
  EXPIRE_PAYMENT_WITH_PAYPAL: 'transition/expire-payment-with-paypal',

  // The backend automatically expire the transaction.
  EXPIRE: 'transition/expire',
  ACCEPT: 'transition/accept',

  // Admin can also cancel the transition.
  CANCEL: 'transition/cancel-as-admin',
  CANCEL_AS_HOST: 'transition/cancel-as-host',
  CANCEL_AS_CUSTOMER: 'transition/cancel-as-customer',

  // The customer can cancel the transaction.
  FULL_CANCELLATION: 'transition/full-cancellation',
  PARTIAL_CANCELLATION: 'transition/partial-cancellation',
  NO_CANCELLATION: 'transition/no-cancellation',
  FULL_CANCELLATION_WITH_PAYPAL: 'transition/full-cancellation-with-paypal',
  PARTIAL_CANCELLATION_WITH_PAYPAL: 'transition/partial-cancellation-with-paypal',
  NO_CANCELLATION_WITH_PAYPAL: 'transition/no-cancellation-with-paypal',

  // The host can cancel the transaction.
  FULL_CANCELLATION_BY_HOST: 'transition/full-cancellation-by-host',
  FULL_CANCELLATION_BY_HOST_WITH_PAYPAL: 'transition/full-cancellation-by-host-with-paypal',

  // The backend will mark the transaction completed.
  COMPLETE: 'transition/complete',
  COMPLETE_WITH_PAYPAL: 'transition/complete-with-paypal',
  CANCEL_AS_HOST_WITH_PAYPAL: 'transition/cancel-as-host-with-paypal',
  CANCEL_AS_CUSTOMER_WITH_PAYPAL: 'transition/cancel-as-customer-with-paypal',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_PENDING: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  DECLINED: 'declined',
  ACCEPTED: 'accepted',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
  DELIVERED: 'delivered',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.REQUEST_PAYMENT_WITH_PAYPAL]: states.PENDING_PAYMENT,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY_WITH_PAYPAL]: states.PENDING_PAYMENT,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.EXPIRE_PAYMENT_WITH_PAYPAL]: states.PAYMENT_EXPIRED,
        [transitions.RECORD_PAYPAL_AUTHORIZATION]: states.PENDING_PAYMENT,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
        [transitions.CONFIRM_PAYMENT_WITH_PAYPAL]: states.PREAUTHORIZED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.UPDATE_PAYPAL_PAYMENT]: states.PREAUTHORIZED,
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.EXPIRE]: states.EXPIRED,
      },
    },

    [states.DECLINED]: {},
    [states.EXPIRED]: {},
    [states.ACCEPTED]: {
      on: {
        [transitions.CANCEL]: states.CANCELED,
        [transitions.CANCEL_AS_HOST]: states.CANCELED,
        [transitions.CANCEL_AS_CUSTOMER]: states.CANCELED,
        [transitions.CANCEL_AS_HOST_WITH_PAYPAL]: states.CANCELED,
        [transitions.CANCEL_AS_CUSTOMER_WITH_PAYPAL]: states.CANCELED,
        [transitions.FULL_CANCELLATION]: states.CANCELED,
        [transitions.PARTIAL_CANCELLATION]: states.CANCELED,
        [transitions.NO_CANCELLATION]: states.CANCELED,
        [transitions.FULL_CANCELLATION_WITH_PAYPAL]: states.CANCELED,
        [transitions.PARTIAL_CANCELLATION_WITH_PAYPAL]: states.CANCELED,
        [transitions.NO_CANCELLATION_WITH_PAYPAL]: states.CANCELED,
        [transitions.FULL_CANCELLATION_BY_HOST]: states.CANCELED,
        [transitions.FULL_CANCELLATION_BY_HOST_WITH_PAYPAL]: states.CANCELED,
        [transitions.COMPLETE]: states.DELIVERED,
        [transitions.COMPLETE_WITH_PAYPAL]: states.DELIVERED,
      },
    },

    [states.CANCELED]: {},
    [states.DELIVERED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.CANCEL,
    transitions.CANCEL_AS_HOST,
    transitions.CANCEL_AS_CUSTOMER,
    transitions.CANCEL_AS_HOST_WITH_PAYPAL,
    transitions.CANCEL_AS_CUSTOMER_WITH_PAYPAL,
    transitions.FULL_CANCELLATION,
    transitions.PARTIAL_CANCELLATION,
    transitions.NO_CANCELLATION,
    transitions.FULL_CANCELLATION_WITH_PAYPAL,
    transitions.PARTIAL_CANCELLATION_WITH_PAYPAL,
    transitions.NO_CANCELLATION_WITH_PAYPAL,
    transitions.FULL_CANCELLATION_BY_HOST,
    transitions.FULL_CANCELLATION_BY_HOST_WITH_PAYPAL,
    transitions.COMPLETE,
    transitions.COMPLETE_WITH_PAYPAL,
    transitions.CONFIRM_PAYMENT,
    transitions.CONFIRM_PAYMENT_WITH_PAYPAL,
    transitions.ACCEPT,
    transitions.EXPIRE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_WITH_PAYPAL,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY_WITH_PAYPAL,
  ].includes(transition);
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.COMPLETE_WITH_PAYPAL,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE_PAYMENT_WITH_PAYPAL,
    transitions.EXPIRE,
    transitions.CANCEL,
    transitions.CANCEL_AS_HOST,
    transitions.CANCEL_AS_CUSTOMER,
    transitions.CANCEL_AS_HOST_WITH_PAYPAL,
    transitions.CANCEL_AS_CUSTOMER_WITH_PAYPAL,
    transitions.FULL_CANCELLATION,
    transitions.PARTIAL_CANCELLATION,
    transitions.NO_CANCELLATION,
    transitions.FULL_CANCELLATION_WITH_PAYPAL,
    transitions.PARTIAL_CANCELLATION_WITH_PAYPAL,
    transitions.NO_CANCELLATION_WITH_PAYPAL,
    transitions.FULL_CANCELLATION_BY_HOST,
    transitions.FULL_CANCELLATION_BY_HOST_WITH_PAYPAL,
  ];
  return txRefundedTransitions.includes(transition);
};

export const txIsAccepted = tx =>
  [
    transitions.CONFIRM_PAYMENT,
    transitions.CONFIRM_PAYMENT_WITH_PAYPAL,
    transitions.UPDATE_PAYPAL_PAYMENT,
  ].includes(tx?.attributes?.lastTransition);
export const txIsPartialCancellation = tx =>
  [transitions.PARTIAL_CANCELLATION, transitions.PARTIAL_CANCELLATION_WITH_PAYPAL].includes(
    tx?.attributes?.lastTransition
  );

export const statesNeedingProviderAttention = [states.PREAUTHORIZED];
